import { useIntl } from 'react-intl';

import { Button, FocusRingVariants, FontWeights } from '@calm-web/design-system';

import jasperLake from '@/../public/_n/images/jasper-lake.webp';
import Image from '@/components/image/NextImage';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAuthModalContext } from '@/context/AuthModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useUserEligibility } from '@/hooks/user/useUserEligibility';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { AmplitudeExperimentNames } from '@/utils/experiments/amplitudeExperiment';

import messages from './messages';
import { AppSection, Body, ButtonContainer, Heading, JasperLake, StickyCopy, Wrapper } from './styles';

const Hero: React.FC = () => {
	const { setAuthPrompt } = useAuthPrompt();
	const routerPush = useRouterPush();
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const { isLoggedOut, isLoggedInEligibleToSubscribe, isLoggedInValid } = useUserEligibility();
	const { setIsModalOpen } = useAuthModalContext();

	// This is the permanent homepage hero design, rendered directly in SSR (it contains the
	// LCP element) rather than gated behind a client-side experiment check.
	const { isEnrolled: isFTUESignupEnrolled } = useAmplitudeExperiment('www-skip-questionnaire');
	const { isEnrolled: isRunwayerQuizEnrolled } = useAmplitudeExperiment(
		AmplitudeExperimentNames.RUNWAYER_QUIZ_ON_CALM_HOME_PAGE,
	);

	const getButtonProperties = (): [
		label: string,
		href:
			| {
					type: 'internal';
					pathname: string;
					queryParams?: Record<string, string | null | undefined>;
			  }
			| { type: 'external'; url: string },
	] => {
		if (isLoggedInValid) {
			return [formatMessage(messages.ctaActiveSub), { type: 'internal', pathname: '/app' }];
		}

		if (isLoggedInEligibleToSubscribe) {
			return [formatMessage(messages.ctaLoggedInIneligible), { type: 'internal', pathname: '/subscribe' }];
		}

		if (isRunwayerQuizEnrolled) {
			const url = new URL('https://quiz.calm.com/calm-ce4a1');
			return [formatMessage(messages.cta), { type: 'external', url: url.toString() }];
		}

		return [
			formatMessage(messages.cta),
			{
				type: 'internal',
				pathname: '/signup-flow',
				queryParams: {
					source: 'homepage',
					...(isFTUESignupEnrolled && { utm_content: 'organic_ft-sf' }),
				},
			},
		];
	};

	const [label, href] = getButtonProperties();

	const onClick = async () => {
		logEvent({
			eventName: 'Homepage : CTA : Clicked',
			eventProps: {
				section: 'hero',
			},
		});
		// Used in UA Tracking
		logEvent({
			eventName: 'Landing Page : Signup Flow CTA : Clicked',
		});
		if (href.type === 'external') {
			window.location.assign(href.url);
		} else {
			routerPush(href.pathname, href.queryParams);
		}
	};

	const onSecondaryClick = async () => {
		logEvent({
			eventName: 'Homepage : CTA : Clicked',
			eventProps: {
				section: 'log in',
			},
		});

		setAuthPrompt('login');
		if (setIsModalOpen) {
			setIsModalOpen(true);
		}
	};

	return (
		<Wrapper>
			<JasperLake>
				<Image priority alt="" loading="eager" fill style={{ objectFit: 'cover' }} src={jasperLake} />
			</JasperLake>
			<AppSection>
				<StickyCopy>
					<Heading>{formatMessage(messages.heading)}</Heading>
					<Body>{formatMessage(messages.bodyShort)}</Body>
					<ButtonContainer>
						<Button
							fullWidth
							onPress={onClick}
							maxWidth="335px"
							fontWeight={FontWeights.Demi}
							backgroundColor="accessibleBrandGradient"
							textColor="white"
							focusRingVariant={FocusRingVariants.Dark}
						>
							{label}
						</Button>
						{isLoggedOut && (
							<Button
								fullWidth
								onPress={onSecondaryClick}
								maxWidth="335px"
								fontWeight={FontWeights.Demi}
								backgroundColor="buttonSecondaryBg"
								textColor="buttonSecondaryText"
								borderColor="blackAlpha20"
							>
								{formatMessage(messages.secondaryCta)}
							</Button>
						)}
					</ButtonContainer>
				</StickyCopy>
			</AppSection>
		</Wrapper>
	);
};

export default Hero;
